import { AppsListPopover, useCurrentApp } from 'modules/Sidebar/components/AppsDropdown';
import { useHasAccessToAppSettings } from 'modules/AppSettings/AppSettings.hooks';
import { useHasAppLevelScopes } from 'modules/Auth/hooks';
import { permissionMatchers } from 'utils/scopes/permissionMatchers';
import { appRelativeHref } from 'modules/Sidebar/SidebarMenuContent';
import { useRouter } from 'next/router';
import getConfig from 'next/config';
import { isIMVForDemo } from 'modules/InstalledModuleVersions/utils/imv_utils';
import { css, cx } from '@linaria/core';
import { baseTheme } from 'styles/utils';
import { Thumbnail } from 'components/Thumbnail';
import { constructCoverImageURL, CoverImageSize } from 'utils/coverImage';
import { PopoverAction, usePopoverState } from 'components/Popover';
import { IconChevronDown, IconChevronUp, IconCube, IconPuzzle } from 'components/Icons';
import { useElementSize } from '@mantine/hooks';
import { queryKeys, useQueryTE } from 'utils/react-query';
import { listInstalledModuleVersions } from 'modules/InstalledModuleVersions/components/IMVSidebarMenu';
import { either, pipe } from 'utils/fp';
import { Divider } from '@mantine/core';
import { ellipsis } from 'polished';
import { Menu, MenuStyles } from './Menu';

/**
 * Dynamic modules are enabled when the MODULE_MANAGER_HOST variable is defined and has a value.
 */
const {
  NEXT_PUBLIC_FIXED_MODULES,
  NEXT_PUBLIC_MODULE_MANAGER_HOST
} = (getConfig().publicRuntimeConfig as {
  NEXT_PUBLIC_FIXED_MODULES?: unknown;
  NEXT_PUBLIC_MODULE_MANAGER_HOST?: string;
});
const FIXED_MODULES = Array.isArray(NEXT_PUBLIC_FIXED_MODULES) ? (NEXT_PUBLIC_FIXED_MODULES as Array<{
  module_url?: string;
  display_name?: string;
}>) : [];
const MODULE_MANAGER_HOST = NEXT_PUBLIC_MODULE_MANAGER_HOST;
const DYNAMIC_MODULES_ENABLED = MODULE_MANAGER_HOST && MODULE_MANAGER_HOST.length > 0;
const SelectTrigger: React.FC<{
  currentApp?: CF.API.Apps.App;
  isPopoverOpen?: boolean;
}> = ({
  currentApp,
  isPopoverOpen
}) => {
  const ChevronIcon = isPopoverOpen ? IconChevronUp : IconChevronDown;
  const overrideStyles: React.CSSProperties = currentApp ? {} : {
    backgroundColor: '#E4E7EC',
    backgroundImage: 'none'
  };
  return <>
      <Thumbnail src={currentApp && constructCoverImageURL(currentApp, CoverImageSize.SMALL)} data-testid="app-thumbnail" fallback={currentApp?.id} className={AppSelectorStyles.appThumbnail} style={overrideStyles}>
        {!currentApp?.id && <IconCube size={16} color={baseTheme.light.colors.gray500} />}
      </Thumbnail>
      <span className="content">{currentApp?.id || 'Select Application'}</span>
      <ChevronIcon size={20} className={AppSelectorStyles.chevronIcon} />
    </>;
};
const SwitchApp: React.FC = () => {
  const popover = usePopoverState({
    placement: 'bottom-start'
  });
  const currentApp = useCurrentApp();
  const {
    ref,
    width
  } = useElementSize();
  return <>
      <PopoverAction data-width={width} ref={ref} popover={popover} className={cx(MenuStyles.groupHeader, 'menu-group-header', AppSelectorStyles.switchApp)}>
        <SelectTrigger currentApp={currentApp} isPopoverOpen={popover.visible} />
      </PopoverAction>
      <AppsListPopover popover={popover} style={{
      minWidth: width || undefined
    }} />
    </>;
};
export const AppSelector: React.FC = () => {
  const router = useRouter();
  const {
    appId = '',
    userOrOrgId
    // installedModuleVersionId,
  } = (router.query as {
    appId: string;
    userOrOrgId: string;
    installedModuleVersionId?: string;
  });

  // TODO: Ensure only the search params are carried forward and not all query params of router.
  const asPathSplit = router.asPath.split('?');
  const searchParams = asPathSplit.length > 1 ? new URLSearchParams(asPathSplit[1]) : {};
  const currentApp = useCurrentApp();
  const hasCurrentApp = Boolean(currentApp);
  const appHref = appRelativeHref(userOrOrgId, appId);
  const hasAccessToAppSettings = useHasAccessToAppSettings();
  const hasAccessToInput = useHasAppLevelScopes([permissionMatchers.inputs.list]);
  const hasAccessToTask = useHasAppLevelScopes([permissionMatchers.tasks.list]);
  const hasAccessToDataset = useHasAppLevelScopes([permissionMatchers.datasets.list]);
  const hasAccessToModuleVersions = useHasAppLevelScopes([permissionMatchers.imvs.list]);
  const showInstalledModules = Boolean(DYNAMIC_MODULES_ENABLED || isIMVForDemo(userOrOrgId));
  const showFixedModules = Boolean(!DYNAMIC_MODULES_ENABLED && FIXED_MODULES?.length);
  const fixedModuleMenuItems = (FIXED_MODULES.map(moduleItem => {
    const moduleUrl = moduleItem?.module_url;
    if (typeof moduleUrl !== 'string' || moduleUrl.length === 0) return null;
    const displayName = typeof moduleItem?.display_name === 'string' && moduleItem.display_name.length > 0 ? moduleItem.display_name : moduleUrl;
    return {
      key: moduleUrl,
      href: appHref(`/installed_module_versions?module_url=${encodeURIComponent(moduleUrl)}&display_name=${encodeURIComponent(displayName)}`, searchParams),
      label: displayName
    };
  }).filter(Boolean) as Array<{
    key: string;
    href: string;
    label: string;
  }>);
  const {
    data: installedModuleVersionsE
  } = useQueryTE([queryKeys.Modules, {
    userOrOrgId,
    appId
  }], listInstalledModuleVersions(userOrOrgId, appId), {
    enabled: hasAccessToModuleVersions && Boolean(userOrOrgId) && Boolean(appId)
  });
  const installedModuleVersions = pipe(installedModuleVersionsE, either.fold(() => [], imv => imv));
  const hasInstalledModuleVersions = installedModuleVersions.length > 0;
  return <div className={cx(AppSelectorStyles.group, 'app-selector')}>
      <SwitchApp />
      <Menu.Item href={appHref(undefined, searchParams)} visible={hasCurrentApp}>
        Overview
      </Menu.Item>
      <Menu.Item matchInitialHrefForHighlighting href={appHref('/settings', searchParams)} visible={hasAccessToAppSettings}>
        Settings
      </Menu.Item>
      <Menu.Item matchInitialHrefForHighlighting href={appHref('/inputs', searchParams)} visible={hasAccessToInput}>
        Inputs
      </Menu.Item>
      <Menu.Item matchInitialHrefForHighlighting href={appHref('/datasets', searchParams)} visible={hasAccessToDataset}>
        Datasets
      </Menu.Item>
      <Menu.Item matchInitialHrefForHighlighting href={appHref('/models', searchParams)} visible={hasCurrentApp}>
        Models
      </Menu.Item>
      <Menu.Item matchInitialHrefForHighlighting href={appHref('/workflows', searchParams)} visible={hasCurrentApp}>
        Workflows
      </Menu.Item>
      <Menu.Item matchInitialHrefForHighlighting href={appHref('/modules', searchParams)} visible={hasCurrentApp && Boolean(DYNAMIC_MODULES_ENABLED)}>
        Modules
      </Menu.Item>
      <Menu.Item matchInitialHrefForHighlighting href={appHref('/tasks', searchParams)} visible={hasAccessToTask}>
        Labeling Tasks
      </Menu.Item>

      {showInstalledModules && hasInstalledModuleVersions && <>
          <Divider color={baseTheme.light.colors.gray200} />
          <Menu.Item href={appHref('/installed_module_versions/module_manager_install/manage_imvs', searchParams)} icon={<IconPuzzle size={18} />}>
            Manage Installed Modules
          </Menu.Item>
        </>}
      {showFixedModules && fixedModuleMenuItems.map(item => <Menu.Item key={item.key} href={item.href}>
            {item.label}
          </Menu.Item>)}
      {installedModuleVersions.map(installedModuleVersion => <Menu.Group key={installedModuleVersion.id} title={installedModuleVersion.module_version.module_nav.title} className={AppSelectorStyles.nestedGroup} highlightOnHref={`/installed_module_versions/${installedModuleVersion.id}`}>
          {installedModuleVersion.module_version.module_nav.module_sub_navs.map(subNav => <Menu.Item key={`${installedModuleVersion.id}-${subNav.query_value}`} href={appHref(`/installed_module_versions/${installedModuleVersion.id}${subNav.query_value.trim() ? `/${subNav.query_value}` : ''}`, searchParams)} className="no-margin">
              {subNav.title}
            </Menu.Item>)}
        </Menu.Group>)}
    </div>;
};
const AppSelectorStyles = {
  group: "omponents__SidebarV2__AppSelector_tsx-group-gibtyhw",
  nestedGroup: "omponents__SidebarV2__AppSelector_tsx-nestedGroup-n1426iqq",
  appThumbnail: "omponents__SidebarV2__AppSelector_tsx-appThumbnail-arg4p76",
  switchApp: "omponents__SidebarV2__AppSelector_tsx-switchApp-sbvjqdp",
  chevronIcon: "omponents__SidebarV2__AppSelector_tsx-chevronIcon-c98zoki"
};

require("./AppSelector.linaria.module.css!=!../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./AppSelector.tsx");